import {
  AppLayout,
  Button,
  Checkbox,
  Container,
  ContentLayout,
  Flashbar,
  FormField,
  Header,
  HelpPanel,
  Input,
  Link,
  SpaceBetween,
  Textarea,
  TextContent,
} from '@amzn/awsui-components-react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmbassadorOffboardingHelp } from './ambassadorOffboardingHelp';
import { useOffboardAmbassador } from '../../hooks/ambassadors/ambassadorCreation/offboardAmbassadorMutation';
import { useUser } from '../../hooks/user';

interface AmbassadorOffboardingFormProps {
  setSubmitted: Dispatch<SetStateAction<boolean>>;
}

export function AmbassadorOffboardingForm({ setSubmitted }: AmbassadorOffboardingFormProps) {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const loggedInUser = useUser();

  const [failed, setFailed] = useState(false);
  const [showBeforeYouGoSection, setShowBeforeYouGoSection] = useState(false);

  const [tooTimeConsuming, setTooTimeConsuming] = useState<string | undefined>(undefined);
  const [rotatesAmbassadors, setRotatesAmbassadors] = useState<string | undefined>(undefined);
  const [iDontEnjoyThisRole, setIDontEnjoyThisRole] = useState<string | undefined>(undefined);
  const [imChangingTeams, setImChangingTeams] = useState<string | undefined>(undefined);
  const [imNoLongerAMemberOfAnEmployeeLedGroup, setImNoLongerAMemberOfAnEmployeeLedGroup] =
    useState<string | undefined>(undefined);
  const [imMovingSites, setImMovingSites] = useState<string | undefined>(undefined);

  const [feedback, setFeedback] = useState('');
  const [nomineeName, setNomineeName] = useState('');
  const [whyGoodFit, setWhyGoodFit] = useState<string | undefined>('');

  const offboardAmbassador = useOffboardAmbassador();

  const isValid = useMemo(() => {
    const hasPickedAReason =
      tooTimeConsuming ||
      rotatesAmbassadors ||
      iDontEnjoyThisRole ||
      imChangingTeams ||
      imNoLongerAMemberOfAnEmployeeLedGroup ||
      imMovingSites;

    return hasPickedAReason;
  }, [
    tooTimeConsuming,
    rotatesAmbassadors,
    iDontEnjoyThisRole,
    imChangingTeams,
    imNoLongerAMemberOfAnEmployeeLedGroup,
    imMovingSites,
  ]);

  const isLoading = offboardAmbassador.isPending;

  const onSubmit = () => {
    const offboardingReasons = [
      tooTimeConsuming,
      rotatesAmbassadors,
      iDontEnjoyThisRole,
      imChangingTeams,
      imNoLongerAMemberOfAnEmployeeLedGroup,
      imMovingSites,
    ].filter(Boolean) as string[];

    if (loggedInUser.employeePersonId) {
      offboardAmbassador.mutate(
        {
          personId: loggedInUser.employeePersonId,
          offboardingReasons: offboardingReasons,
          offboardingAdditionalFeedback: feedback,
          offboardingNomineeAlias: nomineeName,
          offboardingNomineeJustification: whyGoodFit,
        },
        {
          onSuccess: () => {
            setSubmitted(true);
          },
          onError: (err) => {
            setFailed(true);
            console.error(`Error offboarding ambassador: ${err}`);
          },
        }
      );
    } else {
      setFailed(true);
      console.error(`Error offboarding ambassador: No Person ID found for current user`);
    }
  };

  return (
    <AppLayout
      navigationHide
      toolsOpen={showBeforeYouGoSection}
      onToolsChange={() => setShowBeforeYouGoSection((s) => !s)}
      tools={
        <HelpPanel header={<h2>{t('ambassadorOffboarding.help.beforeYouGo')}</h2>}>
          {<AmbassadorOffboardingHelp />}
        </HelpPanel>
      }
      notifications={
        <>
          <Flashbar
            items={
              failed
                ? [
                    {
                      type: 'error',
                      dismissible: true,
                      content: t('ambassadorOffboarding.error'),
                      id: 'message_1',
                    },
                  ]
                : []
            }
          />
        </>
      }
      content={
        <ContentLayout
          header={
            <Header
              variant="h1"
              description={t('ambassadorOffboarding.thankYouForYourMeaningfulContributions')}
              info={
                <Link variant="info" onClick={() => setShowBeforeYouGoSection((s) => !s)}>
                  {t('ambassadorOffboarding.info')}
                </Link>
              }
            >
              {t('ambassadorOffboarding.leaveTheCommunityAmbassadorProgram')}
            </Header>
          }
        >
          <Container
            header={
              <Header variant="h2" description={t('ambassadorOffboarding.wereSorryToSeeYouGo')}>
                {t('ambassadorOffboarding.leaveTheAmbassadorProgram')}
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="m">
              <hr data-volunteer-center-hr />
              <FormField label={t('ambassadorOffboarding.whyAreYouLeavingTheProgram')}>
                <SpaceBetween direction="vertical" size={'s'}>
                  <Checkbox
                    onChange={({ detail }) =>
                      setTooTimeConsuming((v) =>
                        v ? undefined : t('ambassadorOffboarding.tooTimeConsuming')
                      )
                    }
                    checked={!!tooTimeConsuming}
                  >
                    {t('ambassadorOffboarding.tooTimeConsuming')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setRotatesAmbassadors((v) =>
                        v ? undefined : t('ambassadorOffboarding.myTeamRotatesAmbassadors')
                      )
                    }
                    checked={!!rotatesAmbassadors}
                  >
                    {t('ambassadorOffboarding.myTeamRotatesAmbassadors')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setIDontEnjoyThisRole((v) =>
                        v ? undefined : t('ambassadorOffboarding.iDontEnjoyThisRole')
                      )
                    }
                    checked={!!iDontEnjoyThisRole}
                  >
                    {t('ambassadorOffboarding.iDontEnjoyThisRole')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setImChangingTeams((v) =>
                        v ? undefined : t('ambassadorOffboarding.imChangingTeams')
                      )
                    }
                    checked={!!imChangingTeams}
                  >
                    {t('ambassadorOffboarding.imChangingTeams')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setImNoLongerAMemberOfAnEmployeeLedGroup((v) =>
                        v
                          ? undefined
                          : t('ambassadorOffboarding.imNoLongerAMemberOfAnEmployeeLedGroup')
                      )
                    }
                    checked={!!imNoLongerAMemberOfAnEmployeeLedGroup}
                  >
                    {t('ambassadorOffboarding.imNoLongerAMemberOfAnEmployeeLedGroup')}
                  </Checkbox>
                  <Checkbox
                    onChange={({ detail }) =>
                      setImMovingSites((v) =>
                        v ? undefined : t('ambassadorOffboarding.imMovingSites')
                      )
                    }
                    checked={!!imMovingSites}
                  >
                    {t('ambassadorOffboarding.imMovingSites')}
                  </Checkbox>
                </SpaceBetween>
              </FormField>
              <FormField
                stretch
                label={t('ambassadorOffboarding.additionalFeedback')}
                description={t('ambassadorOffboarding.optional')}
              >
                <Textarea
                  onChange={({ detail }) => setFeedback(detail.value)}
                  value={feedback}
                  placeholder={t('ambassadorOffboarding.tellUsMoreAboutYourExperience')}
                />
                <TextContent>
                  <small>{t('ambassadorOffboarding.yourFeedback')}</small>
                </TextContent>
              </FormField>

              <div>
                <FormField
                  label={t('ambassadorOffboarding.nominate')}
                  description={t('ambassadorOffboarding.optional')}
                />
                <div
                  className="ambassador-representation-checkbox"
                  data-testid="ambassador-representation-checkbox"
                >
                  <SpaceBetween direction="vertical" size="m">
                    <FormField stretch label={t('ambassadorOffboarding.nomineeName')}>
                      <Input
                        placeholder={t('ambassadorOffboarding.enterFullName')}
                        onChange={({ detail }) => setNomineeName(detail.value)}
                        value={nomineeName}
                      />
                    </FormField>
                    <FormField stretch label={t('ambassadorOffboarding.whyWouldTheyBeAGoodFit')}>
                      <Textarea
                        onChange={({ detail }) => setWhyGoodFit(detail.value)}
                        value={whyGoodFit || ''}
                        placeholder={t('ambassadorOffboarding.tellUsAboutYourNominee')}
                      />
                    </FormField>
                  </SpaceBetween>
                </div>
              </div>
              <Button variant="normal" onClick={onSubmit} disabled={!isValid} loading={isLoading}>
                {t('ambassadorOffboarding.leaveAmbassadorProgram')}
              </Button>
            </SpaceBetween>
          </Container>
        </ContentLayout>
      }
    />
  );
}
